import React, { useCallback, useMemo, useState } from 'react';
import { useMutation, useQuery } from '@apollo/client';
import Helmet from 'react-helmet';
import {
  Box,
  Button,
  CircularProgress,
  Container,
  Stack,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import {
  matchPath,
  Route,
  Routes,
  useLocation,
  useParams,
  useSearchParams,
} from 'react-router-dom';

import {
  Breadcrumbs,
  BurgerMenu,
  FieldSessionList,
  FindGallery,
  FindGrid,
  Header,
  Main,
  useFindResultsState,
} from '../../components';
import type { FieldSessionSummaryData } from '../../components';
import {
  DELETE_FIELD_SESSION_MUTATION,
  GET_MY_FIELD_SESSION_QUERY,
  GET_MY_FIELD_SESSION_SUMMARY_QUERY,
  GET_MY_FIELD_SESSIONS_QUERY,
} from '../../gql';
import { ViewModeToggle } from '../Site/SiteActionBar/ViewModeToggle';
import { FieldSessionOverview } from './FieldSessionOverview';
import { FieldSessionsMenu } from './FieldSessionsMenu';
import {
  FIELD_SESSION_PAGE_SIZE,
  FIELD_SESSION_PAGES_PARAM,
  parseFieldSessionPages,
} from './pagination';
import { FormattedMessage, useIntl } from 'react-intl';

const loadingStyles = { py: 8, alignItems: 'center' };
const loadMoreStyles = { mt: 4, mb: 6 };

function FieldSessionsIndex() {
  const intl = useIntl();
  const [searchParams, setSearchParams] = useSearchParams();
  const [deletingId, setDeletingId] = useState<string | null>(null);
  const [deleteError, setDeleteError] = useState('');
  const pages = parseFieldSessionPages(
    searchParams.get(FIELD_SESSION_PAGES_PARAM)
  );
  const loadedCount = pages * FIELD_SESSION_PAGE_SIZE;
  const { data, loading, error: queryError, refetch } = useQuery(
    GET_MY_FIELD_SESSIONS_QUERY,
    {
      variables: { limit: loadedCount + 1 },
      fetchPolicy: 'cache-and-network',
    }
  );
  const [deleteFieldSession] = useMutation(DELETE_FIELD_SESSION_MUTATION);
  const availableSessions = data?.getMyFieldSessions ?? [];
  const sessions = availableSessions.slice(0, loadedCount);
  const hasMore = availableSessions.length > loadedCount;

  const handleLoadMore = useCallback(() => {
    setSearchParams(
      (previous) => {
        const next = new URLSearchParams(previous);
        next.set(FIELD_SESSION_PAGES_PARAM, String(pages + 1));
        return next;
      },
      { replace: true }
    );
  }, [pages, setSearchParams]);

  const handleDelete = useCallback(
    async (session: FieldSessionSummaryData) => {
      const label =
        session.name ||
        intl.formatMessage({
          id: 'fieldSessions.thisSession',
          defaultMessage: 'this field session',
        });
      if (
        !window.confirm(
          intl.formatMessage(
            {
              id: 'fieldSessions.confirmDelete',
              defaultMessage: 'Delete {name}? The linked finds will be kept.',
            },
            { name: label }
          )
        )
      ) {
        return;
      }

      setDeletingId(session.id);
      setDeleteError('');

      try {
        const result = await deleteFieldSession({
          variables: { id: session.id },
        });
        if (!result.data?.deleteFieldSession) {
          throw new Error('The field session could not be deleted.');
        }

        await refetch();
      } catch (error) {
        setDeleteError(
          intl.formatMessage({
            id: 'fieldSessions.deleteError',
            defaultMessage: 'The field session could not be deleted.',
          })
        );
      } finally {
        setDeletingId(null);
      }
    },
    [deleteFieldSession, intl, refetch]
  );

  if (loading && !data) {
    return (
      <Main>
        <Stack sx={loadingStyles}>
          <CircularProgress />
        </Stack>
      </Main>
    );
  }

  return (
    <Main>
      {deleteError && (
        <Typography color="error" mb={2}>
          {deleteError}
        </Typography>
      )}
      {queryError && (
        <Typography color="error" mb={2}>
          <FormattedMessage
            id="fieldSessions.loadError"
            defaultMessage="Unable to load field sessions."
          />
        </Typography>
      )}
      {sessions.length === 0 ? (
        <Typography color="text.secondary" py={4}>
          <FormattedMessage
            id="fieldSessions.empty"
            defaultMessage="No field sessions yet."
          />
        </Typography>
      ) : (
        <FieldSessionList
          sessions={sessions}
          deletingId={deletingId}
          onDelete={handleDelete}
        />
      )}
      <Stack direction="row" justifyContent="center" sx={loadMoreStyles}>
        <Button
          variant="contained"
          disabled={!hasMore || loading}
          type="button"
          onClick={handleLoadMore}
        >
          <FormattedMessage id="action.loadMore" defaultMessage="Load more" />
        </Button>
      </Stack>
    </Main>
  );
}

function FieldSessionFinds({
  sessionId,
  mode,
}: {
  sessionId: string;
  mode: 'grid' | 'gallery';
}) {
  const state = useFindResultsState();
  const { data, loading, error } = useQuery(GET_MY_FIELD_SESSION_QUERY, {
    variables: { id: sessionId, filters: state.filter },
    fetchPolicy: 'cache-and-network',
  });
  const finds = data?.getMyFieldSession?.finds ?? [];

  if (error) {
    return (
      <Typography color="error">
        <FormattedMessage
          id="fieldSessions.findsLoadError"
          defaultMessage="Unable to load session finds."
        />
      </Typography>
    );
  }

  if (mode === 'gallery') {
    return <FindGallery finds={finds} loading={loading} state={state} />;
  }

  return (
    <FindGrid rows={finds} loading={loading} state={state} createHref={null} />
  );
}

function FieldSessionDetail({ mode }: { mode: 'grid' | 'gallery' }) {
  const { sessionId } = useParams();
  const { data, loading, error } = useQuery(
    GET_MY_FIELD_SESSION_SUMMARY_QUERY,
    {
      variables: { id: sessionId },
      fetchPolicy: 'cache-first',
      skip: !sessionId,
    }
  );
  const session = data?.getMyFieldSession;

  if (loading && !session) {
    return (
      <Main>
        <Stack sx={loadingStyles}>
          <CircularProgress />
        </Stack>
      </Main>
    );
  }

  if (error) {
    return (
      <Main>
        <FormattedMessage
          id="fieldSessions.detailLoadError"
          defaultMessage="Unable to load this field session."
        />
      </Main>
    );
  }

  if (!session || !sessionId) {
    return (
      <Main>
        <FormattedMessage
          id="fieldSessions.notFound"
          defaultMessage="Field session not found."
        />
      </Main>
    );
  }

  return (
    <Main>
      <Routes>
        <Route index element={<FieldSessionOverview session={session} />} />
        <Route
          path="finds"
          element={<FieldSessionFinds sessionId={sessionId} mode={mode} />}
        />
      </Routes>
    </Main>
  );
}

export const FieldSessions = React.memo(() => {
  const intl = useIntl();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const location = useLocation();
  const [findListMode, setFindListMode] = useState<'grid' | 'gallery'>('grid');
  const sessionMatch = matchPath(
    '/field-sessions/:sessionId/*',
    location.pathname
  );
  const sessionId = sessionMatch?.params.sessionId;
  const findsPath = sessionId ? `/field-sessions/${sessionId}/finds` : null;
  const isFinds = findsPath === location.pathname;
  const menu = useMemo(() => <FieldSessionsMenu sessionId={sessionId} />, [
    sessionId,
  ]);

  return (
    <Box width="100%" height="100%">
      <Helmet
        title={intl.formatMessage({
          id: 'fieldSessions.pageTitle',
          defaultMessage: 'Field sessions | Acolyte',
        })}
      />
      <Header mainIcon={isMobile && <BurgerMenu menu={menu} />} />
      <Container maxWidth="xl">
        <Stack direction="row">
          {!isMobile && menu}
          <Stack flexGrow={1} gap={1} minWidth={0}>
            <Stack
              direction={isMobile ? 'column' : 'row'}
              justifyContent="space-between"
              alignItems={isMobile ? 'flex-start' : 'center'}
            >
              <Breadcrumbs />
              {isFinds && (
                <ViewModeToggle
                  findListMode={findListMode}
                  setFindListMode={setFindListMode}
                />
              )}
            </Stack>
            <Routes>
              <Route index element={<FieldSessionsIndex />} />
              <Route
                path=":sessionId/*"
                element={<FieldSessionDetail mode={findListMode} />}
              />
            </Routes>
          </Stack>
        </Stack>
      </Container>
    </Box>
  );
});
